import { api } from "./api";

const DB_NAME = "ln_api_cache_v1";
const STORE_NAME = "entries";
const IS_DEV = process.env.NODE_ENV === "development";

/** @type {Map<string, { data: unknown, fetchedAt: number }>} */
const memoryFallback = new Map();

/** @type {Map<string, Promise<{ data: unknown, meta: CacheMeta }>>} */
const inflight = new Map();

/**
 * Bumped on every invalidate so in-flight GETs that finish afterward
 * cannot write pre-invalidation payloads back into the cache (e.g. feed
 * still showing checked-in after checkout).
 */
let cacheEpoch = 0;

export const API_CACHE_TTL = {
  FEED_YOUR: IS_DEV ? 5_000 : 60_000,
  FEED_DRAFTS: IS_DEV ? 5_000 : 2 * 60_000,
  BROADCASTS: IS_DEV ? 5_000 : 3 * 60_000,
  DASHBOARD: IS_DEV ? 5_000 : 30_000,
  DASHBOARD_BACKGROUND: IS_DEV ? 3_000 : 15_000,
};

/**
 * Stable cache key from URL + query params (sorted).
 * @param {string} url
 * @param {Record<string, unknown> | undefined} params
 */
export function buildCacheKey(url, params = {}) {
  const pairs = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${String(value)}`);
  return pairs.length ? `${url}?${pairs.join("&")}` : url;
}

function isFresh(entry, ttlMs) {
  return Boolean(entry && Date.now() - entry.fetchedAt < ttlMs);
}

let dbPromise = null;

function openDb() {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE_NAME);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    });
  }
  return dbPromise;
}

async function idbGet(key) {
  const db = await openDb();
  if (!db) return memoryFallback.get(key) || null;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result || memoryFallback.get(key) || null);
    req.onerror = () => resolve(memoryFallback.get(key) || null);
  });
}

async function idbSet(key, entry) {
  memoryFallback.set(key, entry);
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(entry, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

async function idbDelete(key) {
  memoryFallback.delete(key);
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

async function idbClearMatching(matcher) {
  const keys = [...memoryFallback.keys()].filter(matcher);
  await Promise.all(keys.map((key) => idbDelete(key)));

  const db = await openDb();
  if (!db) return;

  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      if (matcher(cursor.key)) store.delete(cursor.key);
      cursor.continue();
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

/**
 * @param {string} url
 * @param {Record<string, unknown> | undefined} params
 * @returns {Promise<{ data: unknown, fetchedAt: number } | null>}
 */
export async function readApiCache(url, params) {
  const key = buildCacheKey(url, params);
  const entry = await idbGet(key);
  if (!entry || typeof entry.fetchedAt !== "number") return null;
  return entry;
}

/**
 * @typedef {{ fromCache: boolean, stale: boolean }} CacheMeta
 */

/**
 * GET with IndexedDB cache, in-flight deduplication, and offline fallback.
 * @param {string} url
 * @param {{ params?: Record<string, unknown>, signal?: AbortSignal, ttlMs?: number, force?: boolean, useSinceTs?: boolean }} [options]
 * @returns {Promise<{ data: unknown, meta: CacheMeta & { unchanged?: boolean } }>}
 */
export async function cachedGet(url, {
  params = {},
  signal,
  ttlMs = 60_000,
  force = false,
  useSinceTs = false,
} = {}) {
  // since_ts is a request hint only — never part of the cache key.
  const { since_ts: _ignoredSinceTs, ...keyParams } = params;
  const key = buildCacheKey(url, keyParams);
  // Capture before any await so an invalidate mid-flight can't be mistaken for our epoch.
  const startedEpoch = cacheEpoch;
  const cached = !force ? await idbGet(key) : null;

  if (!force && isFresh(cached, ttlMs)) {
    return { data: cached.data, meta: { fromCache: true, stale: false } };
  }

  if (!force && cached && typeof navigator !== "undefined" && !navigator.onLine) {
    return { data: cached.data, meta: { fromCache: true, stale: true } };
  }

  if (!force && inflight.has(key)) {
    return inflight.get(key);
  }

  const request = (async () => {
    try {
      const requestParams = { ...keyParams };
      if (useSinceTs && !force && cached?.data?.server_ts) {
        requestParams.since_ts = cached.data.server_ts;
      }
      const response = await api.get(url, { params: requestParams, signal });
      let data = response.data;
      let unchanged = false;
      if (data?.unchanged && cached?.data) {
        unchanged = true;
        data = {
          ...cached.data,
          server_ts: data.server_ts || cached.data.server_ts,
          unchanged: true,
        };
      }
      // Skip write if any cache was invalidated while we were in flight.
      if (startedEpoch === cacheEpoch) {
        await idbSet(key, { data, fetchedAt: Date.now() });
      }
      return { data, meta: { fromCache: false, stale: false, unchanged } };
    } catch (err) {
      if (cached) {
        return { data: cached.data, meta: { fromCache: true, stale: true } };
      }
      throw err;
    } finally {
      inflight.delete(key);
    }
  })();

  if (!force) inflight.set(key, request);
  return request;
}

/**
 * @param {(key: string) => boolean} matcher
 */
export async function invalidateApiCache(matcher) {
  cacheEpoch += 1;
  for (const key of [...inflight.keys()]) {
    if (!matcher || matcher(key)) inflight.delete(key);
  }
  await idbClearMatching(matcher);
}

export function invalidateFeedCache() {
  return invalidateApiCache((key) => key === "/feed" || key.startsWith("/feed?"));
}

export function invalidateAllApiCache() {
  return invalidateApiCache(() => true);
}

export function invalidateBroadcastsCache() {
  return invalidateApiCache((key) =>
    key === "/broadcasts"
    || key.startsWith("/broadcasts?")
    || key === "/notifications"
    || key.startsWith("/notifications?"));
}

export function invalidateDashboardCache(eventId) {
  const prefix = `/events/${eventId}/dashboard`;
  return invalidateApiCache((key) => key === prefix || key.startsWith(`${prefix}?`));
}
